import {
  CommentPost,
  Reaction,
  ReactionType,
  VideoModel,
} from '@loomhq/createhq/dist/video-player';

import emojiMap from 'constants/emojiReactions';
import { CAM } from 'constants/recordingTypes';
import { LOOM_URI } from 'constants/runtimeConfig';
import { USER } from 'constants/ssr';
import { getCloudfrontURI } from 'utilities/avatar';

type VideoFromServer = any;

export type GraphQlReactioFromServer = {
  id: string;
  time: number;
  reaction: number;
  user?: {
    display_name: string;
    id: string;
  };
  anon_user_id?: string;
  anon_user_name?: string;
  locallyCreated?: boolean;
};

export type CommentFromServer = {
  id: string;
  createdAt: string;
  content: string;
  plainContent: string;
  time_stamp?: number;
  user_name: string;
  user_id?: number;
  anon_user_id?: number;
  children_comments: ReplyFromServer[];
  avatar: {
    name: string;
    thumb: string;
  } | null;
  locallyDeleted?: boolean;
};

export type ReplyFromServer = {
  id: string;
  createdAt: string;
  content: string;
  plainContent: string;
  comment_post_id: string;
  user_name: string;
  user_id?: number;
  anon_user_id?: number;
  avatar: {
    name: string;
    thumb: string;
  };
  locallyDeleted?: boolean;
};

export type CommentOrReply = CommentFromServer | ReplyFromServer;

export const isReply = (comment: CommentOrReply): boolean => {
  return !(comment as CommentFromServer).children_comments;
};

export const shouldFlipVideo = (
  video: VideoFromServer | VideoFromGraphQl
): boolean =>
  video.flipped_camera && video.video_properties.screen_type === CAM;

const user: any = window[USER] || {};

export const userContext = {
  baseUrl: 'https://' + LOOM_URI,
  isLoggedUser: Boolean(user.id),
};

export const parseGraphqlReaction = (
  reaction: GraphQlReactioFromServer,
  viewingUserId: number
): Reaction => {
  const displayName = reaction.user
    ? reaction.user.display_name
    : reaction.anon_user_name;

  const authedCanDelete = Boolean(
    reaction?.user?.id === viewingUserId.toString()
  );
  const anonCanDelete = Boolean(
    reaction?.anon_user_id === viewingUserId.toString()
  );

  return {
    id: reaction.id,
    type: emojiMap[reaction.reaction] as ReactionType,
    time: reaction.time / 1000,
    name: displayName || 'Anonymous',
    canDelete: authedCanDelete || anonCanDelete,
    isNew: Boolean(reaction.locallyCreated),
  };
};

const excludeLocallyDeleted = (
  commentOrReply: CommentFromServer | ReplyFromServer
) => {
  return !commentOrReply.locallyDeleted;
};

export const parseComment = (comment: CommentFromServer): CommentPost => {
  return {
    id: comment.id,
    content: comment.plainContent,
    time: comment.time_stamp ? comment.time_stamp : 0,
    name: comment.user_name,
    createdAt: new Date(comment.createdAt),
    avatar: comment?.avatar?.thumb && getCloudfrontURI(comment.avatar.thumb),
    hasMore: comment.children_comments.filter(excludeLocallyDeleted).length,
  };
};

// parseComments will be sending comments into the createhq player
// the createhq player does not need to know about locally deleted comments or replies,
// they are only used on the overlay
export const parseComments = (comments: CommentFromServer[]): CommentPost[] => {
  return comments.filter(excludeLocallyDeleted).map(parseComment);
};

export function parseVideo(video: VideoFromServer): VideoModel {
  video.defaultThumbnails = {
    ...video.defaultThumbnails,
    preview: video.signedThumbnails?.['animated-preview'],
  };

  const thumbnails = prependCdn(video.defaultThumbnails) as {
    default: string;
    static: string;
    preview: string;
  };

  const properties = video.video_properties;

  const flipVideo = shouldFlipVideo(video);

  const videoFeatureFlags = video.video_feature_flags;

  videoFeatureFlags.configHlsJsPlayer =
    video.video_feature_flags['config-hls-js-player'];
  delete videoFeatureFlags['config-hls-js-player'];

  return {
    id: video.id,
    title: video.name,
    // @ts-expect-error ignore due to enabling strict null checks
    src: null,
    source: undefined,
    owner: {
      name: video.owner_full_name,
      displayName: video.owner_name,
      avatar: getCloudfrontURI(video.owner_avatar?.thumb),
    },
    isOwner: video.current_user_is_owner,
    cta: parseCta(video.cta),
    thumbnails,
    views: video.total_views,
    videoProperties: {
      width: properties.width,
      height: properties.height,
      duration: properties.duration,
      recordingVersion: properties.recording_version,
      os: properties.os,
    },
    reactions: [],
    videoFeatureFlags,
    processingInformation: {
      trimId: video.processing_information.trim_id,
      instantEditingEnabled:
        video.processing_information.instant_editing_enabled,
    },
    // TODO: VV: When we support custom thumbnails, this will be (flipVideo && !hasCustomThumb)
    flipThumbnail: flipVideo,
    flipVideo,
    noAccess: false,
    commentsEnabled: video.comments_enabled,
    reactionsEnabled: video.use_emojis,
    needsPassword: Boolean(video.needs_password),
  };
}

export type VideoFromGraphQl = {
  __typename:
    | 'RegularUserVideo'
    | 'PrivateVideo'
    | 'VideoPasswordMissingOrIncorrect';
  id: string;
  name: string;
  signedDefaultThumbnails: {
    default: string;
    static: string;
  };
  signedThumbnails: {
    animatedPreview: string;
  };
  owner: {
    display_name: string;
    avatars: { thumb: string }[];
  };
  totalComments: number;
  views: {
    total: number;
  };
  processing_information?: {
    trim_id?: string;
    instant_editing_enabled?: boolean;
  };
  video_feature_flags: unknown;
  video_properties?: {
    os?: string;
    recording_version?: string;
    width: number;
    height: number;
    duration: number;
    screen_type: string;
  };
  cta?: LegacyCta;
  flipped_camera: boolean;
  video_reactions: GraphQlReactioFromServer[];
  comments_enabled: boolean;
  use_emojis: boolean;
  needs_password: boolean;
};

export type ModelError = {
  id: string;
  noAccess: boolean;
  needsPassword: boolean;
};

export function parseGraphQLVideo(
  video: VideoFromGraphQl
): VideoModel | ModelError {
  if (
    !video ||
    video.__typename == 'PrivateVideo' ||
    video.__typename == 'VideoPasswordMissingOrIncorrect'
  ) {
    return {
      id: video.id,
      noAccess: video.__typename == 'PrivateVideo',
      needsPassword: video.__typename == 'VideoPasswordMissingOrIncorrect',
    };
  }

  const properties = video.video_properties;
  const thumbnails = prependCdn({
    default: video.signedDefaultThumbnails.default,
    static: video.signedDefaultThumbnails.static,
    preview: video.signedThumbnails.animatedPreview,
  }) as {
    default: string;
    static: string;
    preview: string;
  };

  const flipVideo = shouldFlipVideo(video);

  return {
    id: video.id,
    title: video.name,
    // @ts-expect-error ignore due to enabling strict null checks
    src: null,
    source: undefined,
    owner: {
      name: video.owner?.display_name,
      avatar: getCloudfrontURI(video.owner?.avatars?.[0].thumb),
    },
    cta: parseCta(video.cta),
    thumbnails,
    views: video.views.total,
    videoProperties: {
      // @ts-expect-error ignore due to enabling strict null checks
      width: properties.width,
      // @ts-expect-error ignore due to enabling strict null checks
      height: properties.height,
      // @ts-expect-error ignore due to enabling strict null checks
      duration: properties.duration,
      // @ts-expect-error ignore due to enabling strict null checks
      recordingVersion: properties.recording_version,
      // @ts-expect-error ignore due to enabling strict null checks
      os: properties.os,
    },
    reactions: [],
    videoFeatureFlags: video.video_feature_flags as JSON,
    processingInformation: {
      trimId: null,
      instantEditingEnabled: Boolean(
        video.processing_information?.instant_editing_enabled
      ),
    },
    flipThumbnail: flipVideo,
    flipVideo,
    noAccess: false,
    commentsEnabled: video.comments_enabled,
    reactionsEnabled: video.use_emojis,
    needsPassword: Boolean(video.needs_password),
  };
}

function prependCdn(object) {
  const obj = {};

  Object.keys(object).forEach(k => {
    obj[k] = getCloudfrontURI(object[k]);
  });

  return obj;
}

type LegacyCta = {
  url: string;
  mods: {
    color: string;
    border_radius: number;
    background_color: string;
  };
  text: string;
  enabled: boolean;
};
type FreshCta = VideoModel['cta'];

// @ts-expect-error ignore due to enabling strict null checks
const borderMapping: Record<number, FreshCta['radiusVariant']> = {
  25: 'rounded',
  0: 'squared',
  4: 'slightlyRounded',
};

function parseCta(cta?: LegacyCta): FreshCta | undefined {
  if (!cta || !cta.enabled) {
    return;
  }

  const freshCta: FreshCta = {
    url: cta.url,
    text: cta.text,
  };

  if (!cta.mods) {
    return freshCta;
  }

  freshCta.contentColor = cta.mods.color;
  freshCta.backgroundColor = cta.mods.background_color;
  freshCta.radiusVariant = borderMapping[cta.mods.border_radius];

  return freshCta;
}
